import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createUser($data: createUserInput!) {
    createUser(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createUserRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateUserInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'user/create_user',
  async (params: {data: TYPES.CreateUserInput;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createUser;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createUserSlice = createSlice({
  name: 'create_user',
  initialState: initialState,
  reducers: {
    createUserClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createUserRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createUserRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createUserRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createUserClear } = createUserSlice.actions;

export const createUser = createSelector(
  (state: RootState) => state.user.createUser.result,
  (state: RootState) => state.user.createUser.error,
  (state: RootState) => state.user.createUser.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createUserSlice.reducer;